import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import {
  makeStyles,
  useTheme,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  InputAdornment,
  Collapse,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Tabs,
  Tab,
  Select,
  MenuItem,
  LinearProgress,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Page, Content } from '@backstage/core-components';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { useQuayData } from './QuayContext';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  return {
    toolbar: {
      display: 'flex',
      gap: theme.spacing(2.5),
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      marginBottom: theme.spacing(3.5),
    },
    searchField: {
      minWidth: 280,
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'transparent',
        transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${isDark ? 'rgba(77,163,255,0.15)' : 'rgba(0,120,212,0.1)'}`,
        },
      },
    },
    formControl: { minWidth: 200 },
    tableContainer: {
      borderRadius: 14,
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      boxShadow: 'none',
      background: isDark ? '#222428' : '#ffffff',
      overflow: 'hidden',
      '& .MuiTableCell-head': {
        fontWeight: 700,
        fontSize: '10.5px',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: isDark ? '#6e7479' : '#8c9196',
        background: isDark ? '#1e2024' : '#f7f8fa',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
        padding: '12px 20px',
      },
      '& .MuiTableCell-body': {
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
        padding: '13px 20px',
        fontSize: '13px',
        color: isDark ? '#e4e6e8' : '#111318',
      },
      '& .MuiTableRow-root:hover': {
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
      },
    },
    repoName: {
      fontWeight: 600,
      cursor: 'pointer',
      color: isDark ? '#e4e6e8' : theme.palette.text.primary,
      transition: 'color 0.2s ease, text-decoration 0.2s ease',
      '&:hover': {
        textDecoration: 'underline',
        color: '#4da3ff',
      },
    },
    tagRow: {
      backgroundColor: isDark ? '#232629' : '#f9fafb',
    },
    tagTable: {
      marginLeft: theme.spacing(5),
    },
    vuln: {
      fontWeight: 700,
      fontSize: '11px',
      borderRadius: 8,
      height: 22,
    },
    emptyState: {
      textAlign: 'center' as const,
      padding: theme.spacing(6),
      color: isDark ? '#6e7479' : theme.palette.text.secondary,
      '& svg': {
        fontSize: 48,
        opacity: 0.25,
        marginBottom: theme.spacing(2),
      },
    },
    expandCell: { width: 40, padding: '8px 10px' },
    watchedActive: { color: '#ffb366' },
    watchedInactive: { opacity: 0.35 },
    filterBar: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      flexWrap: 'wrap' as const,
      marginBottom: 24,
    },
    statCard: {
      flex: '1 1 160px',
      minWidth: 140,
      maxWidth: 220,
      background: isDark ? '#222428' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      borderRadius: 12,
      padding: '20px 24px',
      transition: 'border-color 0.18s ease, transform 0.18s ease',
      '&:hover': {
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
        transform: 'translateY(-1px)',
      },
    },
    statCardClickable: {
      cursor: 'pointer',
    },
    statCardActive: {
      borderColor: `${isDark ? '#4da3ff' : '#0078d4'} !important`,
      background: isDark
        ? 'rgba(77,163,255,0.06) !important'
        : 'rgba(0,120,212,0.04) !important',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.1,
      color: isDark ? '#e4e6e8' : '#111318',
    },
    statLabel: {
      fontSize: '11px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: '#6e7479',
      marginTop: 8,
      fontWeight: 500,
    },
    sectionTitle: {
      fontWeight: 700,
      fontSize: '1.1rem',
      marginBottom: 20,
      letterSpacing: '-0.2px',
      color: isDark ? '#e4e6e8' : theme.palette.text.primary,
    },
    overviewCard: {
      height: '100%',
      borderRadius: 16,
      border: `1px solid ${isDark ? '#414141' : theme.palette.divider}`,
      boxShadow: isDark
        ? '0 2px 8px rgba(0,0,0,0.15)'
        : '0 1px 4px rgba(0,0,0,0.04)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        borderColor: '#4da3ff',
        transform: 'translateY(-3px)',
        boxShadow: isDark
          ? '0 8px 24px rgba(77,163,255,0.12)'
          : '0 6px 16px rgba(77,163,255,0.08)',
      },
    },
    overviewCardContent: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 10,
      padding: 20,
      height: '100%',
    },
    overviewRepoName: {
      fontWeight: 700,
      fontSize: '1rem',
      letterSpacing: '-0.1px',
      color: isDark ? '#e4e6e8' : theme.palette.text.primary,
    },
    overviewNsLabel: {
      fontSize: '11px',
      color: '#6e7479',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    overviewTagName: {
      fontSize: '13px',
      color: isDark ? '#a0a4a8' : theme.palette.text.secondary,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
});

const VULN_COLORS: Record<string, { background: string; color: string }> = {
  Critical:  { background: '#b71c1c', color: '#ef9a9a' },
  High:      { background: '#e65100', color: '#ffccbc' },
  Medium:    { background: '#f57f17', color: '#fff9c4' },
  Low:       { background: '#1b5e20', color: '#a5d6a7' },
  None:      { background: '#1a237e', color: '#c5cae9' },
  Queued:    { background: '#424242', color: '#bdbdbd' },
};

const QUAY_UI = 'https://quay.hydra.verint.com';

function formatDate(ts: string | number | undefined): string {
  if (!ts) return '—';
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatSize(bytes: number | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

type VulnSummary = { Critical: number; High: number; Medium: number; Low: number; status: string };
type CveDetail = { name: string; severity: string; packageName: string; currentVersion: string; fixedBy: string; link: string; description: string };
type Tag = { name: string; manifest_digest: string; last_modified: string; size: number; vuln?: VulnSummary; cves?: CveDetail[] }

interface OverviewItem {
  namespace: string;
  repoName: string;
  lastModified?: number;
  latestTag?: { name: string; vuln?: VulnSummary };
}

/** Returns the worst CVE severity label for a tag, or 'clean' / 'scanning'. */
function worstSeverity(vuln: VulnSummary | undefined): string {
  if (!vuln) return 'scanning';
  if (vuln.status !== 'scanned') return vuln.status;
  if (vuln.Critical > 0) return 'Critical';
  if (vuln.High > 0) return 'High';
  if (vuln.Medium > 0) return 'Medium';
  if (vuln.Low > 0) return 'Low';
  return 'clean';
}

export const QuayRepositoriesPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  // Repos/orgs/loading/error/watchedRepos are pre-fetched at app start via QuayProvider
  const { repos, orgs, loading, error, watchedRepos, refresh: loadRepos, toggleWatch } = useQuayData();

  const [selectedOrg, _setSelectedOrg] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<0 | 1>(0); // 0=My Subscriptions, 1=Browse
  const [selectedRepo, setSelectedRepo] = useState<{ namespace: string; repoName: string } | null>(null);
  const [repoDetailTags, setRepoDetailTags] = useState<Tag[]>([]);
  const [repoDetailLoading, setRepoDetailLoading] = useState(false);
  const [expandedTagNames, setExpandedTagNames] = useState<Set<string>>(new Set());
  const [overviewItems, setOverviewItems] = useState<OverviewItem[]>([]);
  const [overviewFilter, setOverviewFilter] = useState<'all' | 'subscribed' | 'clean' | 'issues'>('all');
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewSearch, setOverviewSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Tag subscriptions from localStorage (quay-tag-sub:{ns}/{repo}/{tag})
  const [tagSubbedRepos, setTagSubbedRepos] = useState(() => {
    const keys = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('quay-tag-sub:')) {
        const parts = k.slice('quay-tag-sub:'.length).split('/');
        // format: ns / repoName / tag  (repoName may contain slashes)
        if (parts.length >= 3) keys.add(`${parts[0]}/${parts.slice(1, -1).join('/')}`);
      }
    }
    return keys;
  });

  // Unsubscribe: removes from DB-watched + clears localStorage tag subs for that repo
  const handleUnwatch = useCallback((namespace: string, repoName: string) => {
    // Remove all localStorage quay-tag-sub keys for this ns/repo
    const prefix = `quay-tag-sub:${namespace}/${repoName}/`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    // Update local state so card disappears immediately
    setTagSubbedRepos(prev => {
      const next = new Set(prev);
      next.delete(`${namespace}/${repoName}`);
      return next;
    });
    // Also remove from DB-watched list if present
    if (watchedRepos.some(w => w.namespace === namespace && w.repoName === repoName)) {
      toggleWatch(namespace, repoName);
    }
  }, [toggleWatch, watchedRepos]);

  // Combined list: DB-watched repos + tag-subscribed repos
  const allSubbedRepos = useMemo(() => {
    const watched = watchedRepos.map(w => ({ namespace: w.namespace, repoName: w.repoName }));
    const watchedKeys = new Set(watchedRepos.map(w => `${w.namespace}/${w.repoName}`));
    const tagOnly = [...tagSubbedRepos]
      .filter(key => !watchedKeys.has(key))
      .map(key => { const idx = key.indexOf('/'); return { namespace: key.slice(0, idx), repoName: key.slice(idx + 1) }; });
    return [...watched, ...tagOnly];
  }, [watchedRepos, tagSubbedRepos]);

  const proxyBase = useCallback(async () => {
    const base = await discoveryApi.getBaseUrl('portal');
    return `${base}/quay`;
  }, [discoveryApi]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = debounceRef.current;
    clearTimeout(debounceTimer);
    return () => clearTimeout(debounceTimer);
  }, [search]);

  /** Load all tags + full vuln data for the repo detail view */
  const loadRepoDetail = useCallback(async (namespace: string, repoName: string) => {
    setRepoDetailLoading(true);
    setRepoDetailTags([]);
    try {
      const base = await proxyBase();
      const resp = await fetchApi.fetch(`${base}/api/v1/repository/${namespace}/${repoName}/tag/?limit=20&onlyActiveTags=true&_t=${Date.now()}`, { cache: 'no-store' });
      if (!resp.ok) return;
      const data = await resp.json();
      const tags: Tag[] = (data.tags ?? []).map((t: any) => ({
        name: t.name,
        manifest_digest: t.manifest_digest ?? '',
        last_modified: t.last_modified ?? '',
        size: t.size,
      }));
      await Promise.all(tags.map(async tag => {
        if (!tag.manifest_digest) return;
        try {
          const vr = await fetchApi.fetch(`${base}/api/v1/repository/${namespace}/${repoName}/manifest/${tag.manifest_digest}/security?vulnerabilities=true&_t=${Date.now()}`, { cache: 'no-store' });
          if (!vr.ok) return;
          const vd = await vr.json();
          const status: string = vd.status ?? 'Queued';
          if (status !== 'scanned') { tag.vuln = { Critical: 0, High: 0, Medium: 0, Low: 0, status }; return; }
          const counts = { Critical: 0, High: 0, Medium: 0, Low: 0, status: 'scanned' };
          const cves: CveDetail[] = [];
          for (const layer of (vd.data?.Layer?.Features ?? [])) {
            for (const v of (layer.Vulnerabilities ?? [])) {
              const sev: string = v.Severity ?? 'Unknown';
              if (sev in counts) (counts as any)[sev]++;
              cves.push({
                name: v.Name ?? '',
                severity: sev,
                packageName: layer.Name ?? '',
                currentVersion: layer.Version ?? '',
                fixedBy: v.FixedBy ?? '',
                link: (v.Link ?? '').split(' ')[0],
                description: v.Description ?? '',
              });
            }
          }
          tag.vuln = counts;
          tag.cves = cves.sort((a, b) => {
            const order = ['Critical', 'High', 'Medium', 'Low'];
            const ai = order.indexOf(a.severity);
            const bi = order.indexOf(b.severity);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          });
        } catch { /* ignore */ }
      }));
      setRepoDetailTags(tags);
    } catch { /* ignore */ } finally {
      setRepoDetailLoading(false);
    }
  }, [fetchApi, proxyBase]);

  useEffect(() => {
    if (selectedRepo) loadRepoDetail(selectedRepo.namespace, selectedRepo.repoName);
  }, [selectedRepo, loadRepoDetail]);

  /** Load latest tag + vuln for each subscribed repo (DB-watched or tag-subscribed) for the overview cards */
  const loadOverview = useCallback(async () => {
    if (allSubbedRepos.length === 0) { setOverviewItems([]); return; }
    setOverviewLoading(true);
    try {
      const base = await proxyBase();
      const results = await Promise.allSettled(
        allSubbedRepos.map(async ({ namespace, repoName }) => {
          const repoMeta = repos.find(r => r.namespace === namespace && r.name === repoName);
          try {
            const tr = await fetchApi.fetch(`${base}/api/v1/repository/${namespace}/${repoName}/tag/?limit=5&onlyActiveTags=true&_t=${Date.now()}`, { cache: 'no-store' });
            if (!tr.ok) return { namespace, repoName, lastModified: repoMeta?.last_modified };
            const td = await tr.json();
            const first = td.tags?.[0];
            if (!first) return { namespace, repoName, lastModified: repoMeta?.last_modified };
            let vuln: VulnSummary | undefined;
            if (first.manifest_digest) {
              try {
                const vr = await fetchApi.fetch(`${base}/api/v1/repository/${namespace}/${repoName}/manifest/${first.manifest_digest}/security?vulnerabilities=true&_t=${Date.now()}`, { cache: 'no-store' });
                if (vr.ok) {
                  const vd = await vr.json();
                  const status: string = vd.status ?? 'Queued';
                  if (status === 'scanned') {
                    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0, status: 'scanned' };
                    for (const layer of (vd.data?.Layer?.Features ?? [])) {
                      for (const v of (layer.Vulnerabilities ?? [])) {
                        const sev: string = v.Severity ?? 'Unknown';
                        if (sev in counts) (counts as any)[sev]++;
                      }
                    }
                    vuln = counts;
                  } else {
                    vuln = { Critical: 0, High: 0, Medium: 0, Low: 0, status };
                  }
                }
              } catch { /* ignore */ }
            }
            return { namespace, repoName, lastModified: repoMeta?.last_modified, latestTag: { name: first.name, vuln } };
          } catch {
            return { namespace, repoName, lastModified: repoMeta?.last_modified };
          }
        }),
      );
      setOverviewItems(
        results
          .filter(r => r.status === 'fulfilled')
          .map(r => (r as PromiseFulfilledResult<OverviewItem>).value),
      );
    } finally {
      setOverviewLoading(false);
    }
  }, [allSubbedRepos, repos, fetchApi, proxyBase]); // eslint-disable-line

  // Reload overview when subscribed repos or repo list changes
  useEffect(() => {
    if (activeTab === 0 && !selectedRepo) loadOverview();
  }, [allSubbedRepos, repos, activeTab, selectedRepo, loadOverview]);

  // ── Browse-specific state ─────────────────────────────────────────────────
  const [browseFilterBy, setBrowseFilterBy] = useState<'all' | 'orgs' | 'repos'>('all');
  const [browseExpandedOrgs, setBrowseExpandedOrgs] = useState<Set<string>>(new Set());
  const [browseExpandedRepos, setBrowseExpandedRepos] = useState<Set<string>>(new Set());
  const [browseTagsLoading, setBrowseTagsLoading] = useState<Record<string, boolean>>({});
  const browseTagsCacheRef = useRef<Record<string, { name: string; lastModified?: string }[]>>({});
  const [browsePage, setBrowsePage] = useState(0);
  const [browsePerPage, setBrowsePerPage] = useState(25);
  const [watchedTags, setWatchedTags] = useState<Set<string>>(() => {
    const keys = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('quay-tag-sub:')) keys.add(k.slice('quay-tag-sub:'.length));
    }
    return keys;
  });

  const loadBrowseTags = useCallback(async (repoKey: string) => {
    if (browseTagsCacheRef.current[repoKey] !== undefined) return;
    const slashIdx = repoKey.indexOf('/');
    if (slashIdx === -1) return;
    const ns = repoKey.slice(0, slashIdx);
    const repoName = repoKey.slice(slashIdx + 1);
    setBrowseTagsLoading(prev => ({ ...prev, [repoKey]: true }));
    try {
      const base = await proxyBase();
      const resp = await fetchApi.fetch(`${base}/api/v1/repository/${ns}/${repoName}/tag/?limit=100&onlyActiveTags=true&_t=${Date.now()}`, { cache: 'no-store' });
      if (!resp.ok) { browseTagsCacheRef.current[repoKey] = []; return; }
      const data = await resp.json();
      browseTagsCacheRef.current[repoKey] = (data.tags ?? []).map((t: any) => ({ name: t.name, lastModified: t.last_modified ?? '' }));
    } catch {
      browseTagsCacheRef.current[repoKey] = [];
    } finally {
      setBrowseTagsLoading(prev => ({ ...prev, [repoKey]: false }));
    }
  }, [fetchApi, proxyBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBrowseOrgExpand = useCallback((org: string) => {
    setBrowseExpandedOrgs(prev => { const n = new Set(prev); n.has(org) ? n.delete(org) : n.add(org); return n; });
  }, []);

  const toggleBrowseRepoExpand = useCallback((repoKey: string) => {
    const alreadyOpen = browseExpandedRepos.has(repoKey);
    setBrowseExpandedRepos(prev => { const n = new Set(prev); n.has(repoKey) ? n.delete(repoKey) : n.add(repoKey); return n; });
    if (!alreadyOpen) loadBrowseTags(repoKey);
  }, [browseExpandedRepos, loadBrowseTags]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRepoWatched = useCallback(
    (ns: string, name: string) => watchedRepos.some(w => w.namespace === ns && w.repoName === name),
    [watchedRepos],
  );

  const isTagWatched = useCallback(
    (ns: string, repoName: string, tag: string) => watchedTags.has(`${ns}/${repoName}/${tag}`),
    [watchedTags],
  );

  const toggleTagWatch = useCallback((ns: string, repoName: string, tag: string) => {
    const subKey = `${ns}/${repoName}/${tag}`;
    const lsKey = `quay-tag-sub:${subKey}`;
    setWatchedTags(prev => {
      const next = new Set(prev);
      if (next.has(subKey)) {
        next.delete(subKey);
        localStorage.removeItem(lsKey);
        const repoHasOtherTags = [...next].some(k => k.startsWith(`${ns}/${repoName}/`));
        if (!repoHasOtherTags) {
          setTagSubbedRepos(prev2 => { const n2 = new Set(prev2); n2.delete(`${ns}/${repoName}`); return n2; });
        }
      } else {
        next.add(subKey);
        localStorage.setItem(lsKey, '1');
        setTagSubbedRepos(prev2 => { const n2 = new Set(prev2); n2.add(`${ns}/${repoName}`); return n2; });
      }
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderBellIcon = (active: boolean, partial?: boolean) => {
    if (active) return <NotificationsActiveIcon fontSize="small" style={{ color: '#f5a623' }} />;
    if (partial) return <NotificationsActiveIcon fontSize="small" style={{ color: '#90caf9' }} />;
    return <NotificationsNoneIcon fontSize="small" />;
  };

  const toggleOrgWatch = useCallback(async (org: string) => {
    const orgRepos = repos.filter(r => r.namespace === org);
    const shouldWatch = !orgRepos.every(r => isRepoWatched(r.namespace, r.name));
    for (const r of orgRepos) {
      const currently = isRepoWatched(r.namespace, r.name);
      if (shouldWatch && !currently) await toggleWatch(r.namespace, r.name);
      else if (!shouldWatch && currently) await toggleWatch(r.namespace, r.name);
    }
  }, [repos, isRepoWatched, toggleWatch]);

  // Overview-level stats
  const cleanCount = overviewItems.filter(i => worstSeverity(i.latestTag?.vuln) === 'clean').length;
  const issueCount = overviewItems.filter(i => {
    const w = worstSeverity(i.latestTag?.vuln);
    return w !== 'clean' && w !== 'scanning';
  }).length;

  const filteredOverviewItems = overviewItems.filter(i => {
    const matchNs = !selectedOrg || i.namespace === selectedOrg;
    const q = overviewSearch.toLowerCase();
    const matchSearch = !q || i.repoName.toLowerCase().includes(q) || i.namespace.toLowerCase().includes(q);
    const w = worstSeverity(i.latestTag?.vuln);
    const matchFilter =
      overviewFilter === 'all' || overviewFilter === 'subscribed' ? true
      : overviewFilter === 'clean' ? w === 'clean'
      : (w !== 'clean' && w !== 'scanning'); // 'issues'
    return matchNs && matchSearch && matchFilter;
  });

  // ─────────────────────────────────────────────
  return (
    <Page themeId="tool">
      <Content>
        {/* ── repo detail view (overlays both tabs) ── */}
        {selectedRepo ? (
          <>
            <Box className={classes.toolbar}>
              <Tooltip title="Back">
                <IconButton size="small" onClick={() => setSelectedRepo(null)} style={{ marginRight: 4 }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography variant="h6" style={{ lineHeight: 1.2 }}>{selectedRepo.repoName}</Typography>
                <Typography variant="caption" color="textSecondary">{selectedRepo.namespace}</Typography>
              </Box>
              <Box style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                <Tooltip title="Open in Quay">
                  <IconButton
                    size="small"
                    href={`${QUAY_UI}/repository/${selectedRepo.namespace}/${selectedRepo.repoName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => loadRepoDetail(selectedRepo.namespace, selectedRepo.repoName)}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {repoDetailLoading && (
              <Box display="flex" alignItems="center" style={{ gap: 8, padding: 16 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">Loading tags and vulnerability data…</Typography>
              </Box>
            )}

            {!repoDetailLoading && repoDetailTags.length === 0 && (
              <Box className={classes.emptyState}>
                <Typography variant="body1">No tags found for this repository.</Typography>
              </Box>
            )}

            {!repoDetailLoading && repoDetailTags.length > 0 && (
              <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.expandCell} />
                      <TableCell>Tag</TableCell>
                      <TableCell>Digest</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Last Modified</TableCell>
                      <TableCell>Vulnerabilities</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repoDetailTags.map(tag => {
                      const isExpanded = expandedTagNames.has(tag.name);
                      const hasCves = (tag.cves?.length ?? 0) > 0;
                      return [
                        <TableRow key={tag.name} hover>
                          <TableCell className={classes.expandCell}>
                            {hasCves ? (
                              <IconButton size="small" onClick={() => setExpandedTagNames(prev => {
                                const next = new Set(prev);
                                if (next.has(tag.name)) next.delete(tag.name); else next.add(tag.name);
                                return next;
                              })}>
                                {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                              </IconButton>
                            ) : null}
                          </TableCell>
                          <TableCell><code>{tag.name}</code></TableCell>
                          <TableCell>
                            <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                              {tag.manifest_digest ? `${tag.manifest_digest.substring(0, 19)}…` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatSize(tag.size)}</TableCell>
                          <TableCell>{formatDate(tag.last_modified)}</TableCell>
                          <TableCell>
                            {!tag.vuln && <Typography variant="caption" color="textSecondary">—</Typography>}
                            {tag.vuln && tag.vuln.status !== 'scanned' && (
                              <Chip label={tag.vuln.status} size="small" style={VULN_COLORS.Queued} className={classes.vuln} />
                            )}
                            {tag.vuln && tag.vuln.status === 'scanned' && (
                              <Box display="flex" style={{ gap: 4, flexWrap: 'wrap' }}>
                                {(['Critical', 'High', 'Medium', 'Low'] as const).map(sev =>
                                  tag.vuln![sev] > 0 && (
                                    <Chip key={sev} label={`${sev}: ${tag.vuln![sev]}`}
                                      size="small" style={VULN_COLORS[sev]} className={classes.vuln}
                                      onClick={hasCves ? () => setExpandedTagNames(prev => {
                                        const next = new Set(prev); next.add(tag.name); return next;
                                      }) : undefined}
                                    />
                                  )
                                )}
                                {(['Critical', 'High', 'Medium', 'Low'] as const).every(s => tag.vuln![s] === 0) && (
                                  <Chip label="No CVEs" size="small" style={VULN_COLORS.None} className={classes.vuln} />
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>,
                        <TableRow key={`${tag.name}-cves`}>
                          <TableCell colSpan={6} style={{ padding: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box p={2} pl={6}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell style={{ fontWeight: 600 }}>CVE</TableCell>
                                      <TableCell style={{ fontWeight: 600 }}>Severity</TableCell>
                                      <TableCell style={{ fontWeight: 600 }}>Package</TableCell>
                                      <TableCell style={{ fontWeight: 600 }}>Version</TableCell>
                                      <TableCell style={{ fontWeight: 600 }}>Fixed By</TableCell>
                                      <TableCell style={{ fontWeight: 600 }}>Description</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(tag.cves ?? []).map(cve => (
                                      <TableRow key={cve.name} className={classes.tagRow}>
                                        <TableCell>
                                          {cve.link ? (
                                            <a href={cve.link} target="_blank" rel="noopener noreferrer"
                                              style={{ color: '#90caf9', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                              {cve.name}
                                            </a>
                                          ) : (
                                            <Typography variant="caption" style={{ fontFamily: 'monospace' }}>{cve.name}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={cve.severity} size="small"
                                            style={VULN_COLORS[cve.severity] ?? VULN_COLORS.Queued}
                                            className={classes.vuln} />
                                        </TableCell>
                                        <TableCell><code style={{ fontSize: '0.78rem' }}>{cve.packageName}</code></TableCell>
                                        <TableCell><code style={{ fontSize: '0.78rem' }}>{cve.currentVersion || '—'}</code></TableCell>
                                        <TableCell><code style={{ fontSize: '0.78rem', color: cve.fixedBy ? '#a5d6a7' : undefined }}>{cve.fixedBy || 'none'}</code></TableCell>
                                        <TableCell style={{ maxWidth: 320 }}>
                                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {cve.description || '—'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>,
                      ];
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          /* ── tabbed main view ── */
          <Box>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v as 0 | 1)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 24, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}
            >
              <Tab label={`My Subscriptions (${allSubbedRepos.length})`} />
              <Tab label="Subscribe" />
            </Tabs>

            {/* ── My Subscriptions tab ── */}
            {activeTab === 0 && (
              <Box>
                {error && <Box mb={2}><Typography color="error">{error}</Typography></Box>}

                <Typography variant="h6" className={classes.sectionTitle}>At a Glance</Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 16 }} mb={4}>
                  <Box className={classes.statCard}>
                    <Typography className={classes.statValue}>{orgs.length}</Typography>
                    <Typography className={classes.statLabel}>Organizations</Typography>
                  </Box>
                  <Box
                    className={`${classes.statCard} ${classes.statCardClickable} ${overviewFilter === 'subscribed' ? classes.statCardActive : ''}`}
                    onClick={() => setOverviewFilter(f => f === 'subscribed' ? 'all' : 'subscribed')}
                  >
                    <Typography className={classes.statValue}>{allSubbedRepos.length}</Typography>
                    <Typography className={classes.statLabel}>Subscribed Repos{overviewFilter === 'subscribed' ? ' ✓' : ''}</Typography>
                  </Box>
                  <Box
                    className={`${classes.statCard} ${classes.statCardClickable} ${overviewFilter === 'clean' ? classes.statCardActive : ''}`}
                    onClick={() => setOverviewFilter(f => f === 'clean' ? 'all' : 'clean')}
                  >
                    <Typography className={classes.statValue}>{cleanCount}</Typography>
                    <Typography className={classes.statLabel}>No CVEs (latest tag){overviewFilter === 'clean' ? ' ✓' : ''}</Typography>
                  </Box>
                  <Box
                    className={`${classes.statCard} ${classes.statCardClickable} ${overviewFilter === 'issues' ? classes.statCardActive : ''}`}
                    onClick={() => setOverviewFilter(f => f === 'issues' ? 'all' : 'issues')}
                  >
                    <Typography className={classes.statValue}>{issueCount}</Typography>
                    <Typography className={classes.statLabel}>Has CVEs / Other{overviewFilter === 'issues' ? ' ✓' : ''}</Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Filter by name…"
                    value={overviewSearch}
                    onChange={e => setOverviewSearch(e.target.value)}
                    className={classes.searchField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {overviewLoading && <CircularProgress size={20} />}
                </Box>

                {allSubbedRepos.length > 0 && (
                  <>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      Subscribed Repos — Recent Activity
                      {overviewLoading && <CircularProgress size={14} style={{ marginLeft: 8, verticalAlign: 'middle' }} />}
                    </Typography>
                    <Grid container spacing={2}>
                      {filteredOverviewItems.map(item => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`${item.namespace}/${item.repoName}`}>
                          <Card variant="outlined" className={classes.overviewCard}>
                            <CardActionArea
                              component="div"
                              onClick={() => setSelectedRepo({ namespace: item.namespace, repoName: item.repoName })}
                              style={{ height: '100%' }}
                            >
                              <CardContent className={classes.overviewCardContent}>
                                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                  <Typography className={classes.overviewRepoName} noWrap style={{ flex: 1 }}>{item.repoName}</Typography>
                                  <Tooltip title="Unsubscribe">
                                    <IconButton
                                      size="small"
                                      style={{ padding: 2, marginLeft: 4 }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleUnwatch(item.namespace, item.repoName);
                                      }}
                                    >
                                      <NotificationsOffIcon style={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Typography className={classes.overviewNsLabel}>{item.namespace}</Typography>
                                {item.latestTag ? (
                                  <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                    {(['Critical', 'High', 'Medium', 'Low'] as const).map(sev =>
                                      item.latestTag!.vuln && item.latestTag!.vuln.status === 'scanned' && (item.latestTag!.vuln as any)[sev] > 0 && (
                                        <Chip key={sev} label={`${sev}: ${(item.latestTag!.vuln as any)[sev]}`}
                                          size="small" className={classes.vuln} style={VULN_COLORS[sev]} />
                                      )
                                    )}
                                    {item.latestTag.vuln?.status === 'scanned' &&
                                      (['Critical', 'High', 'Medium', 'Low'] as const).every(s => (item.latestTag!.vuln as any)[s] === 0) && (
                                        <Chip label="No CVEs" size="small" className={classes.vuln} style={VULN_COLORS.None} />
                                    )}
                                    {item.latestTag.vuln && item.latestTag.vuln.status !== 'scanned' && (
                                      <Chip label={item.latestTag.vuln.status} size="small" className={classes.vuln} style={VULN_COLORS.Queued} />
                                    )}
                                    {!item.latestTag.vuln && (
                                      <Chip label="scanning…" size="small" className={classes.vuln} style={VULN_COLORS.Queued} />
                                    )}
                                    <Typography className={classes.overviewTagName} style={{ flexBasis: '100%' }}>{item.latestTag.name}</Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">No tags</Typography>
                                )}
                                {item.lastModified ? (
                                  <Typography variant="caption" color="textSecondary">
                                    {formatDate(item.lastModified)}
                                  </Typography>
                                ) : null}
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {allSubbedRepos.length === 0 && !overviewLoading && !loading && (
                  <Box className={classes.emptyState}>
                    <Typography variant="body1" gutterBottom>No subscribed repositories yet.</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Switch to the <strong>Browse</strong> tab to find and subscribe to repositories.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* ── Browse tab ── */}
            {activeTab === 1 && (
              <Box>
                {/* Loading bar */}
                {loading && <LinearProgress style={{ marginBottom: 8, borderRadius: 2 }} />}

                {/* Filter bar */}
                <Box className={classes.filterBar}>
                  <Select
                    value={browseFilterBy}
                    onChange={e => { setBrowseFilterBy(e.target.value as 'all' | 'orgs' | 'repos'); setSearch(''); setBrowsePage(0); }}
                    variant="outlined"
                    style={{ height: 40, minWidth: 200 }}
                    inputProps={{ style: { padding: '10px 32px 10px 12px' } }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="orgs">Organizations</MenuItem>
                    <MenuItem value="repos">Repositories</MenuItem>
                  </Select>
                  <TextField
                    variant="outlined"
                    placeholder={browseFilterBy === 'repos' ? 'Search repositories…' : 'Search organizations…'}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ flex: 1, minWidth: 200 }}
                    InputProps={{
                      style: { height: 40 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={loadRepos}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {error && <Box mb={2}><Typography color="error">{error}</Typography></Box>}

                {/* ── Hierarchical orgs table (All / Orgs filter) ── */}
                {(browseFilterBy === 'all' || browseFilterBy === 'orgs') && (
                  <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
                    <Table size="small" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 48 }} />
                        <col />
                        <col style={{ width: '22%' }} />
                        <col style={{ width: '22%' }} />
                        <col style={{ width: 120 }} />
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>Name</TableCell>
                          <TableCell>Namespace / Path</TableCell>
                          <TableCell>Details</TableCell>
                          <TableCell align="right">Subscribe</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orgs.filter(o => !search || o.toLowerCase().includes(search.toLowerCase())).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" style={{ padding: 40, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)' }}>No organizations found</TableCell>
                          </TableRow>
                        ) : orgs.filter(o => !search || o.toLowerCase().includes(search.toLowerCase())).map(org => {
                          const orgRepos = repos.filter(r => r.namespace === org);
                          const watchedCount = orgRepos.filter(r => isRepoWatched(r.namespace, r.name)).length;
                          const orgFullyWatched = orgRepos.length > 0 && orgRepos.every(r => isRepoWatched(r.namespace, r.name));
                          const isOrgOpen = browseExpandedOrgs.has(org);
                          return (
                            <Fragment key={org}>
                              {/* Org row */}
                              <TableRow hover>
                                <TableCell style={{ padding: '4px 8px' }}>
                                  <IconButton size="small" onClick={() => toggleBrowseOrgExpand(org)}>
                                    {isOrgOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                  </IconButton>
                                </TableCell>
                                <TableCell style={{ fontWeight: 600 }}>
                                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    <Chip label="Organization" size="small" style={{ background: 'rgba(144,202,249,0.15)', color: '#90caf9', fontSize: '0.68rem', height: 18 }} />
                                    {org}
                                  </Box>
                                </TableCell>
                                <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org}</TableCell>
                                <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}>
                                  {orgRepos.length} {orgRepos.length === 1 ? 'repo' : 'repos'} · {watchedCount} subscribed
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title={orgFullyWatched ? 'Unsubscribe all repos' : (orgRepos.length === 0 ? 'No repos' : 'Subscribe all repos')}>
                                    <span>
                                      <IconButton size="small" disabled={orgRepos.length === 0} onClick={() => toggleOrgWatch(org)}>
                                        {renderBellIcon(orgFullyWatched)}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              {/* Repo rows */}
                              {isOrgOpen && orgRepos.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} style={{ paddingLeft: 48, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)' }}>No repositories</TableCell>
                                </TableRow>
                              )}
                              {isOrgOpen && orgRepos.map(repo => {
                                const repoKey = `${repo.namespace}/${repo.name}`;
                                const watched = isRepoWatched(repo.namespace, repo.name);
                                const isRepoOpen = browseExpandedRepos.has(repoKey);
                                const tags = browseTagsCacheRef.current[repoKey];
                                const tagsLoading = !!browseTagsLoading[repoKey];
                                return (
                                  <Fragment key={repoKey}>
                                    {/* Repo row */}
                                    <TableRow hover style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                      <TableCell style={{ padding: '4px 8px', paddingLeft: 20 }}>
                                        <IconButton size="small" onClick={() => toggleBrowseRepoExpand(repoKey)}>
                                          {isRepoOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                        </IconButton>
                                      </TableCell>
                                      <TableCell style={{ fontWeight: 500 }}>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                          <Chip label="Repository" size="small" style={{ background: 'rgba(165,214,167,0.15)', color: '#a5d6a7', fontSize: '0.68rem', height: 18 }} />
                                          <span
                                            className={classes.repoName}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setSelectedRepo({ namespace: repo.namespace, repoName: repo.name })}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedRepo({ namespace: repo.namespace, repoName: repo.name }); }}
                                          >
                                            {repo.name}
                                          </span>
                                        </Box>
                                      </TableCell>
                                      <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {repo.namespace}/{repo.name}
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                          <Chip label={repo.is_public ? 'Public' : 'Private'} size="small"
                                            style={repo.is_public ? { background: '#1b5e20', color: '#a5d6a7', fontSize: '0.68rem', height: 18 } : { background: '#4a148c', color: '#ce93d8', fontSize: '0.68rem', height: 18 }} />
                                          {repo.description && (
                                            <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{repo.description}</Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Tooltip title={watched ? 'Unsubscribe' : 'Subscribe'}>
                                          <IconButton size="small" onClick={() => toggleWatch(repo.namespace, repo.name)}>
                                            {renderBellIcon(watched)}
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                    {/* Tag rows */}
                                    {isRepoOpen && tagsLoading && (
                                      <TableRow>
                                        <TableCell colSpan={5} style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                                          <Box display="flex" justifyContent="center" p={1}><CircularProgress size={16} /></Box>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                    {isRepoOpen && !tagsLoading && tags && tags.length === 0 && (
                                      <TableRow>
                                        <TableCell colSpan={5} style={{ paddingLeft: 64, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>No tags found</TableCell>
                                      </TableRow>
                                    )}
                                    {isRepoOpen && !tagsLoading && tags && tags.map(tag => {
                                      const tagWatched = isTagWatched(repo.namespace, repo.name, tag.name);
                                      return (
                                        <TableRow key={tag.name} hover style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                                          <TableCell style={{ paddingLeft: 44 }} />
                                          <TableCell style={{ fontSize: '0.82rem' }}>
                                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                              <Chip label="Tag" size="small" style={{ background: 'rgba(206,147,216,0.15)', color: '#ce93d8', fontSize: '0.68rem', height: 18 }} />
                                              <span style={{ fontFamily: 'monospace' }}>{tag.name}</span>
                                            </Box>
                                          </TableCell>
                                          <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {repo.namespace}/{repo.name}:{tag.name}
                                          </TableCell>
                                          <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)', fontSize: '0.78rem' }}>
                                            {tag.lastModified ? new Date(tag.lastModified).toLocaleString() : '—'}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Tooltip title={tagWatched ? 'Unsubscribe' : 'Subscribe to tag'}>
                                              <IconButton size="small" onClick={() => toggleTagWatch(repo.namespace, repo.name, tag.name)}>
                                                {renderBellIcon(tagWatched)}
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </Fragment>
                                );
                              })}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* ── Flat repos table (filterBy=repos) ── */}
                {browseFilterBy === 'repos' && (() => {
                  const browseFiltered = repos.filter(r =>
                    !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.namespace.toLowerCase().includes(search.toLowerCase())
                  );
                  const browsePaged = browseFiltered.slice(browsePage * browsePerPage, browsePage * browsePerPage + browsePerPage);
                  return (
                    <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} pt={1.5} pb={0.5}>
                        <Typography variant="body2" style={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}>
                          {browseFiltered.length.toLocaleString()} {browseFiltered.length === 1 ? 'repository' : 'repositories'}
                          {search ? ' matching search' : ''}
                        </Typography>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ width: 40 }} />
                            <TableCell>Repository</TableCell>
                            <TableCell>Organization</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell align="right">Subscribe</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {browsePaged.map(repo => {
                            const repoKey = `${repo.namespace}/${repo.name}`;
                            const watched = isRepoWatched(repo.namespace, repo.name);
                            const isRepoOpen = browseExpandedRepos.has(repoKey);
                            const tags = browseTagsCacheRef.current[repoKey];
                            const tagsLoading = !!browseTagsLoading[repoKey];
                            return (
                              <Fragment key={repoKey}>
                                <TableRow hover>
                                  <TableCell style={{ padding: '4px 8px' }}>
                                    <IconButton size="small" onClick={() => toggleBrowseRepoExpand(repoKey)}>
                                      {isRepoOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell style={{ fontWeight: 500 }}>
                                    <span
                                      className={classes.repoName}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setSelectedRepo({ namespace: repo.namespace, repoName: repo.name })}
                                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedRepo({ namespace: repo.namespace, repoName: repo.name }); }}
                                    >
                                      {repo.name}
                                    </span>
                                  </TableCell>
                                  <TableCell>{repo.namespace}</TableCell>
                                  <TableCell style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                      <Chip label={repo.is_public ? 'Public' : 'Private'} size="small"
                                        style={repo.is_public ? { background: '#1b5e20', color: '#a5d6a7', fontSize: '0.68rem', height: 18 } : { background: '#4a148c', color: '#ce93d8', fontSize: '0.68rem', height: 18 }} />
                                      {repo.description && (
                                        <Typography variant="caption" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{repo.description}</Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title={watched ? 'Unsubscribe' : 'Subscribe'}>
                                      <IconButton size="small" onClick={() => toggleWatch(repo.namespace, repo.name)}>
                                        {renderBellIcon(watched)}
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                                {isRepoOpen && tagsLoading && (
                                  <TableRow>
                                    <TableCell colSpan={5} style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                                      <Box display="flex" justifyContent="center" p={1}><CircularProgress size={16} /></Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {isRepoOpen && !tagsLoading && tags && tags.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={5} style={{ paddingLeft: 64, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>No tags found</TableCell>
                                  </TableRow>
                                )}
                                {isRepoOpen && !tagsLoading && tags && tags.map(tag => {
                                  const tagWatched = isTagWatched(repo.namespace, repo.name, tag.name);
                                  return (
                                    <TableRow key={tag.name} hover style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                                      <TableCell style={{ paddingLeft: 44 }} />
                                      <TableCell style={{ fontSize: '0.82rem' }}>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                          <Chip label="Tag" size="small" style={{ background: 'rgba(206,147,216,0.15)', color: '#ce93d8', fontSize: '0.68rem', height: 18 }} />
                                          <span style={{ fontFamily: 'monospace' }}>{tag.name}</span>
                                        </Box>
                                      </TableCell>
                                      <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.78rem' }}>
                                        {repo.namespace}/{repo.name}:{tag.name}
                                      </TableCell>
                                      <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)', fontSize: '0.78rem' }}>
                                        {tag.lastModified ? new Date(tag.lastModified).toLocaleString() : '—'}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Tooltip title={tagWatched ? 'Unsubscribe' : 'Subscribe to tag'}>
                                          <IconButton size="small" onClick={() => toggleTagWatch(repo.namespace, repo.name, tag.name)}>
                                            {renderBellIcon(tagWatched)}
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </Fragment>
                            );
                          })}
                          {browsePaged.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} align="center" style={{ padding: 32, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)' }}>No repositories found</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      <TablePagination
                        component="div"
                        count={browseFiltered.length}
                        page={browsePage}
                        onPageChange={(_e, p) => setBrowsePage(p)}
                        rowsPerPage={browsePerPage}
                        onRowsPerPageChange={e => { setBrowsePerPage(parseInt(e.target.value, 10)); setBrowsePage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                      />
                    </TableContainer>
                  );
                })()}
              </Box>
            )}
          </Box>
        )}
      </Content>
    </Page>
  );
};
